/* Search — Find in Files dialog + workspace tabs */

import { state, refs, esc, fileIconSvg, getSyncStatus, syncBadgeHtml } from './state.js';
import { api } from './api.js';
import { getAuthMode } from './auth.js';
import { wsSend } from './ws.js';
import { renderCodeLines } from './highlight.js';
import { isPreviewOpen, showPreviewPanel } from './template-preview.js';
import { mountEditor, disposeTabModel, saveCurrentTab, validateCurrentTab, getEditorContent, revealLine, onContentChange, updateStatusBarLang, updateValidationBar, resetValidationState, isTabDirty } from './monaco-editor.js';
import { createStructurePanel, renderStructurePanel } from './structure-panel.js';
import { highlightActiveFileInTree, buildFileActionsForPath, syncInfoFromPath, actionIcon, pushWithFeedback, pullWithFeedback } from './file-tree.js';
import { showPanel, getActivePanel } from './panel-registry.js';
import { showDiffPanel, hideDiffPanel, openDiffForScript } from './diff-panel.js';
import { confirmPushToCloud, confirmPullFromCloud } from './dialogs.js';
import { notify } from './toast.js';
import { renderTableViewer } from './data-wrangler.js';

// ---------------------------------------------------------------------------
// Module-local search state
// ---------------------------------------------------------------------------

let searchQuery = '';
let searchRegex = false;
let searchCaseSensitive = false;
let searchScope = 'all';
let searchResults = null;
let activeMatchIdx = -1;
let allMatchRows = [];
let previewPath = null;
let previewContent = null;
let debounceTimer = null;
let extFilters = { py: true, html: true, json: true };

// ---------------------------------------------------------------------------
// Active-tab helpers (consumed by the View menu and other modules)
// ---------------------------------------------------------------------------

/**
 * Returns the currently-active editor tab object, or null if no tab is active.
 * The tab object contains: id, path, label, language, _content, _structurePanel,
 * _structurePanelHidden, scrollTop, kindLabel, kind (when set).
 */
export function getActiveTab() {
  if (!state.activeWorkspaceTab) return null;
  return state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab) || null;
}

/**
 * Toggle the Structure outline panel for the currently-active tab.
 * Returns true if a tab with a structure panel was toggled, false otherwise.
 */
export function toggleActiveTabStructure() {
  const tab = getActiveTab();
  if (!tab || !tab._structurePanel) return false;
  const sp = tab._structurePanel;
  sp.element.hidden = !sp.element.hidden;
  tab._structurePanelHidden = sp.element.hidden;
  return true;
}

/**
 * Returns true/false if the active tab has a structure panel and it is visible/hidden,
 * or null if no eligible tab is active. The View menu uses this to render checkmarks
 * and enabled-state for the "Structure outline" item.
 */
export function isStructureVisibleForActiveTab() {
  const tab = getActiveTab();
  if (!tab || !tab._structurePanel) return null;
  return tab._structurePanel.element.hidden === false;
}

// ---------------------------------------------------------------------------
// Search dialog — open / close
// ---------------------------------------------------------------------------

export function openSearch() {
  if (!refs.searchDialog) {
    console.error('searchDialog ref not found');
    return;
  }
  refs.searchDialog.show();
  _initSearchDragging();
  refs.searchInput.value = searchQuery;
  refs.searchInput.focus();
  refs.searchInput.select();
}

let _dragInited = false;
function _initSearchDragging() {
  if (_dragInited) return;
  _dragInited = true;
  const dlg = refs.searchDialog;
  const header = dlg.querySelector('.search-header');
  if (!header) return;
  header.style.cursor = 'move';
  let sx = 0, sy = 0, dx = 0, dy = 0, dragging = false;
  let baseX = 0, baseY = 0;
  header.addEventListener('mousedown', (e) => {
    // Don't drag when clicking interactive elements
    if (e.target.closest('input, button, .search-toggle')) return;
    dragging = true;
    sx = e.clientX; sy = e.clientY;
    const rect = dlg.getBoundingClientRect();
    baseX = rect.left; baseY = rect.top;
    // Disable centering transform once drag starts
    dlg.style.transform = 'none';
    dlg.style.left = `${baseX}px`;
    dlg.style.top = `${baseY}px`;
    dlg.classList.add('search-dialog-dragging');
    e.preventDefault();
  });
  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    dx = e.clientX - sx; dy = e.clientY - sy;
    dlg.style.margin = '0';
    dlg.style.left = `${baseX + dx}px`;
    dlg.style.top = `${baseY + dy}px`;
  });
  document.addEventListener('mouseup', () => {
    if (!dragging) return;
    dragging = false;
    dlg.classList.remove('search-dialog-dragging');
  });
}

export function closeSearch() {
  refs.searchDialog.close();
}

// ---------------------------------------------------------------------------
// Search execution
// ---------------------------------------------------------------------------

function scheduleSearch() {
  clearTimeout(debounceTimer);
  const q = refs.searchInput.value.trim();
  searchQuery = q;
  if (!q) {
    searchResults = null;
    activeMatchIdx = -1;
    allMatchRows = [];
    refs.searchResults.innerHTML = '<div class="search-empty">Type to search across all files</div>';
    refs.searchStats.textContent = '';
    clearPreview();
    return;
  }
  debounceTimer = setTimeout(doSearch, 300);
}

async function doSearch() {
  const q = searchQuery;
  if (!q) return;

  // Hosted searches the live SAP corpus per query (a few seconds, not the local index's
  // milliseconds) — set the expectation and show a spinner so "working" never reads as
  // "stuck". The spinner reuses the pipeline's existing .pl-field-spinner animation.
  refs.searchResults.innerHTML = getAuthMode() === 'hosted'
    ? '<div class="search-empty"><span class="pl-field-spinner"></span> Searching your live SAP files. This can take a few seconds.</div>'
    : '<div class="search-empty">Searching...</div>';
  refs.searchStats.textContent = '';

  try {
    const data = await api.searchFiles(q, {
      regex: searchRegex,
      caseSensitive: searchCaseSensitive,
      kind: searchScope,
      max: 200,
    });
    if (searchQuery !== q) return; // stale
    searchResults = data;
    renderResults(data);
  } catch (e) {
    refs.searchResults.innerHTML = `<div class="search-empty">Error: ${esc(e.message)}</div>`;
    refs.searchStats.textContent = '';
  }
}

// ---------------------------------------------------------------------------
// Results rendering
// ---------------------------------------------------------------------------

function renderResults(data) {
  const frag = document.createDocumentFragment();
  allMatchRows = [];
  activeMatchIdx = -1;

  // Filter by extension
  const filtered = data.matches.filter(m => {
    const ext = m.path.split('.').pop();
    return extFilters[ext] !== false;
  });

  if (filtered.length === 0) {
    refs.searchResults.innerHTML = '<div class="search-empty">No results found</div>';
    refs.searchStats.textContent = data.match_count > 0
      ? `${data.match_count} matches filtered out`
      : '';
    return;
  }

  // Group by file
  const groups = new Map();
  for (const m of filtered) {
    const key = m.path;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  }

  for (const [path, matches] of groups) {
    const first = matches[0];
    const groupWrap = document.createElement('div');
    groupWrap.className = 'search-file-group';

    // File group header with chevron (click to collapse/expand)
    const header = document.createElement('div');
    header.className = 'search-file-group-header';
    header.innerHTML = `<span class="search-file-chevron">▾</span>${fileIconSvg(first.language)}
      <span>${esc(first.file)}</span>
      <span class="search-file-group-count">${matches.length}</span>`;
    groupWrap.appendChild(header);

    // Match rows container
    const rowsContainer = document.createElement('div');
    rowsContainer.className = 'search-file-group-rows';
    for (const m of matches) {
      const row = document.createElement('div');
      row.className = 'search-match-row';
      row.innerHTML = `<span class="search-match-line-num">${m.line}</span>
        <span class="search-match-text">${highlightMatch(m.text, searchQuery, searchRegex, searchCaseSensitive)}</span>`;
      const matchInfo = { path: m.path, line: m.line, language: m.language, file: m.file };
      row.addEventListener('click', () => selectMatch(allMatchRows.indexOf(row), matchInfo));
      row.addEventListener('dblclick', () => openFromSearch(matchInfo));
      allMatchRows.push(row);
      rowsContainer.appendChild(row);
    }
    groupWrap.appendChild(rowsContainer);

    // Toggle collapse on header click
    header.addEventListener('click', () => {
      const collapsed = groupWrap.classList.toggle('collapsed');
      header.querySelector('.search-file-chevron').textContent = collapsed ? '▸' : '▾';
    });

    frag.appendChild(groupWrap);
  }

  refs.searchResults.innerHTML = '';
  refs.searchResults.appendChild(frag);

  // Hosted truncation names the real cap (the live corpus fetch budget) instead of the
  // generic "(truncated)" — the honest hint is "narrow the query or kind filter".
  const truncTail = getAuthMode() === 'hosted' ? '(first 80 files searched)' : '(truncated)';
  const stats = data.truncated
    ? `${filtered.length}+ matches in ${groups.size} files ${truncTail}`
    : `${filtered.length} match${filtered.length !== 1 ? 'es' : ''} in ${groups.size} file${groups.size !== 1 ? 's' : ''}`;
  refs.searchStats.textContent = stats;
}

function highlightMatch(text, query, isRegex, caseSensitive) {
  const escaped = esc(text);
  try {
    const flags = caseSensitive ? 'g' : 'gi';
    const pattern = isRegex ? query : query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${pattern})`, flags);
    // Highlight on the escaped text — need to be careful. Simpler approach:
    // split the original text by matches, escape each part, wrap matches in <mark>
    const parts = text.split(new RegExp(pattern, caseSensitive ? 'g' : 'gi'));
    const matches = text.match(new RegExp(pattern, caseSensitive ? 'g' : 'gi')) || [];
    let html = '';
    for (let i = 0; i < parts.length; i++) {
      html += esc(parts[i]);
      if (i < matches.length) {
        html += `<mark>${esc(matches[i])}</mark>`;
      }
    }
    return html;
  } catch {
    return escaped;
  }
}

// ---------------------------------------------------------------------------
// Match selection & preview
// ---------------------------------------------------------------------------

function selectMatch(idx, matchInfo) {
  // Deselect previous
  if (activeMatchIdx >= 0 && allMatchRows[activeMatchIdx]) {
    allMatchRows[activeMatchIdx].classList.remove('active');
  }
  activeMatchIdx = idx;
  if (allMatchRows[idx]) {
    allMatchRows[idx].classList.add('active');
    allMatchRows[idx].scrollIntoView({ block: 'nearest' });
  }
  loadPreview(matchInfo.path, matchInfo.line, matchInfo.language, matchInfo.file);
}

async function loadPreview(path, highlightLine, language, fileName) {
  refs.searchPreviewHeader.hidden = false;
  refs.searchPreviewFilename.textContent = fileName || path.split('/').pop();
  refs.searchPreviewPath.textContent = path;

  if (previewPath === path && previewContent != null) {
    renderPreview(previewContent, language, highlightLine);
    return;
  }

  refs.searchPreviewBody.innerHTML = '<div class="search-empty">Loading...</div>';

  try {
    const result = await api.fileContent(path);
    previewPath = path;
    previewContent = result.content;
    renderPreview(result.content, language, highlightLine);
  } catch (e) {
    refs.searchPreviewBody.innerHTML = `<div class="search-empty">Error: ${esc(e.message)}</div>`;
  }
}

function renderPreview(content, language, highlightLine) {
  refs.searchPreviewBody.innerHTML = '';
  refs.searchPreviewBody.appendChild(renderCodeLines(content, language, highlightLine));
  requestAnimationFrame(() => {
    const hl = refs.searchPreviewBody.querySelector('.code-line.highlight');
    if (hl) hl.scrollIntoView({ block: 'center' });
  });
}

function clearPreview() {
  previewPath = null;
  previewContent = null;
  refs.searchPreviewHeader.hidden = true;
  refs.searchPreviewBody.innerHTML = '<div class="search-empty">Select a result to preview</div>';
}

// ---------------------------------------------------------------------------
// Keyboard navigation
// ---------------------------------------------------------------------------

function handleSearchKeydown(e) {
  if (e.key === 'Escape') {
    closeSearch();
    return;
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (allMatchRows.length === 0) return;
    const next = activeMatchIdx < allMatchRows.length - 1 ? activeMatchIdx + 1 : 0;
    const row = allMatchRows[next];
    if (row) row.click();
    return;
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (allMatchRows.length === 0) return;
    const prev = activeMatchIdx > 0 ? activeMatchIdx - 1 : allMatchRows.length - 1;
    const row = allMatchRows[prev];
    if (row) row.click();
    return;
  }

  if (e.key === 'Enter') {
    e.preventDefault();
    if (activeMatchIdx < 0 || !allMatchRows[activeMatchIdx]) return;
    const matchInfo = getMatchInfoForRow(activeMatchIdx);
    if (!matchInfo) return;
    openFromSearch(matchInfo);
    return;
  }
}

function getMatchInfoForRow(idx) {
  if (!searchResults) return null;
  // filtered matches in display order
  const filtered = searchResults.matches.filter(m => {
    const ext = m.path.split('.').pop();
    return extFilters[ext] !== false;
  });
  // group and flatten to match allMatchRows order
  const groups = new Map();
  for (const m of filtered) {
    const key = m.path;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  }
  const flat = [];
  for (const [, matches] of groups) {
    for (const m of matches) flat.push(m);
  }
  return flat[idx] || null;
}

// ---------------------------------------------------------------------------
// Open file from search (respects checkbox)
// ---------------------------------------------------------------------------

function openFromSearch(matchInfo) {
  closeSearch();
  if (refs.searchOpenInTab.checked) {
    openFileTab(matchInfo.path, matchInfo.file, matchInfo.language, matchInfo.line);
  } else {
    showFileInPlace(matchInfo.path, matchInfo.file, matchInfo.language, matchInfo.line);
  }
}

/**
 * Replace the current view with a file.
 * - If a file tab is active: replaces that tab's content in-place.
 * - If no tab is active: opens the file as a new tab.
 */
async function showFileInPlace(path, name, language, highlightLine) {
  const label = name || path.split('/').pop();

  // If a file tab is currently active, replace its content
  const activeTab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
  if (activeTab) {
    activeTab.path = path;
    activeTab.label = label;
    activeTab.language = language;
    activeTab.highlightLine = highlightLine;
    activeTab._content = null;
    activeTab.scrollTop = 0;
    renderWorkspaceTabs();
    if (refs.fileViewer) refs.fileViewer.innerHTML = '<div class="search-empty">Loading...</div>';
    try {
      const result = await api.fileContent(path);
      activeTab._content = result.content;
      renderFileViewer(activeTab);
    } catch (e) {
      if (refs.fileViewer) refs.fileViewer.innerHTML = `<div class="search-empty">Error: ${esc(e.message)}</div>`;
    }
    return;
  }

  // No active tab — open as a new tab
  openFileTab(path, name, language, highlightLine);
}

// ---------------------------------------------------------------------------
// Split pane resize
// ---------------------------------------------------------------------------

function initSplitResize() {
  const handle = refs.searchSplitHandle;
  if (!handle) return;

  let startY, startResultsH, startPreviewH;

  function onMouseMove(e) {
    const dy = e.clientY - startY;
    const newResultsH = Math.max(80, startResultsH + dy);
    const newPreviewH = Math.max(80, startPreviewH - dy);
    refs.searchResults.style.flex = `0 0 ${newResultsH}px`;
    refs.searchPreview.style.flex = `0 0 ${newPreviewH}px`;
  }

  function onMouseUp() {
    refs.searchDialog.classList.remove('split-resizing');
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }

  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    startY = e.clientY;
    startResultsH = refs.searchResults.getBoundingClientRect().height;
    startPreviewH = refs.searchPreview.getBoundingClientRect().height;
    refs.searchDialog.classList.add('split-resizing');
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });
}

// ---------------------------------------------------------------------------
// Workspace tabs
// ---------------------------------------------------------------------------

/** Kind-folder → user-facing label for the file-viewer kind badge. */
const _KIND_BADGE = {
  'global-scripts':      { kind: 'script',      label: 'Script' },
  'custom-calculations': { kind: 'calculation', label: 'Calculation' },
  'ui-templates':        { kind: 'template',    label: 'Template' },
  'quote-tables':        { kind: 'quote-table', label: 'Quote Table' },
};

function _deriveKind(path) {
  const info = syncInfoFromPath(path, { skipProperties: false });
  if (info) return _KIND_BADGE[info.kind] || null;
  if (path && (path.includes('/properties/') || path.endsWith('.json'))) {
    return { kind: 'properties', label: 'Properties' };
  }
  return null;
}

export function openFileTab(path, name, language, highlightLine) {
  // Check if already open
  let tab = state.workspaceTabs.find(t => t.path === path);
  if (tab) {
    tab.highlightLine = highlightLine || tab.highlightLine;
    switchWorkspaceTab(tab.id);
    return;
  }

  state.wsTabIdCounter++;
  const kindInfo = _deriveKind(path);
  tab = {
    id: 'tab-' + state.wsTabIdCounter,
    label: name || path.split('/').pop(),
    path,
    language,
    kind: kindInfo?.kind || null,
    kindLabel: kindInfo?.label || null,
    highlightLine: highlightLine || null,
    scrollTop: 0,
  };
  state.workspaceTabs.push(tab);
  renderWorkspaceTabs();
  switchWorkspaceTab(tab.id);
  loadFileTabContent(tab);
}

export function closeFileTab(tabId) {
  const idx = state.workspaceTabs.findIndex(t => t.id === tabId);
  if (idx === -1) return;

  // Dispose Monaco model if loaded
  disposeTabModel(tabId);

  state.workspaceTabs.splice(idx, 1);

  if (state.activeWorkspaceTab === tabId) {
    if (state.workspaceTabs.length > 0) {
      const next = state.workspaceTabs[Math.min(idx, state.workspaceTabs.length - 1)];
      switchWorkspaceTab(next.id);
    } else {
      switchWorkspaceTab(null);
    }
  }

  renderWorkspaceTabs();
}

export function switchWorkspaceTab(tabId) {
  // Ignore legacy 'chat' calls — chat is always visible in the right zone
  if (tabId === 'chat') return;

  // Save scroll position of current file tab
  if (state.activeWorkspaceTab) {
    const fv = refs.fileViewer;
    const body = fv && fv.querySelector('.file-viewer-body');
    const prevTab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
    if (prevTab && body) prevTab.scrollTop = body.scrollTop;
  }

  state.activeWorkspaceTab = tabId;
  window.dispatchEvent(new CustomEvent('active-tab-changed', { detail: { tabId } }));

  // Hide validation bar and reset state when switching tabs
  const vbar = document.getElementById('validationBar');
  if (vbar) vbar.hidden = true;
  resetValidationState();

  // If switching to a file tab, ensure the editor panel is the active center panel
  if (tabId && getActivePanel('center') !== 'editor') {
    showPanel('editor');
  }

  renderWorkspaceTabs();

  if (!tabId) {
    // No file tabs open — clear the file viewer
    if (refs.fileViewer) refs.fileViewer.innerHTML = '';
  } else {
    const tab = state.workspaceTabs.find(t => t.id === tabId);
    if (tab) {
      if (tab.type === 'table') {
        renderTableViewer(tab);
      } else {
        renderFileViewer(tab);
        // Auto-update preview when switching to an HTML template tab
        if (tab.path && tab.path.startsWith('ui-templates/') && tab.path.endsWith('.html')) {
          _syncTabToPreview(tab);
        }
      }
    }
  }
  highlightActiveFileInTree(true);
}

/** Push the active template tab's content to the preview panel. */
async function _syncTabToPreview(tab) {
  try {
    const result = await api.fileContent(tab.path);
    const html = result.content || '';
    const name = tab.label.replace(/\.html$/, '');
    await fetch('/api/template/preview/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ html, name, path: tab.path }),
    });
    if (!isPreviewOpen()) showPreviewPanel();
  } catch { /* ignore — preview is supplementary */ }
}

// ---- Tab bar rendering ----

export function renderWorkspaceTabs() {
  const tabBar = refs.zoneCenterTabs;
  if (!tabBar) return;

  const diffActive = state.diffPanelOpen;
  const hasTabs = state.workspaceTabs.length > 0 || diffActive;

  if (!hasTabs) {
    tabBar.innerHTML = '';
    return;
  }

  const frag = document.createDocumentFragment();

  for (const tab of state.workspaceTabs) {
    const isActive = !diffActive && state.activeWorkspaceTab === tab.id;
    const btn = document.createElement('button');
    const dirty = isTabDirty(tab.id);
    btn.className = 'ws-tab' + (isActive ? ' active' : '') + (dirty ? ' dirty' : '');
    btn.dataset.tabId = tab.id;

    // Sync badge in tab (not applicable for table tabs)
    const sInfo = tab.type !== 'table' ? syncInfoFromPath(tab.path) : null;
    const sStatus = sInfo ? getSyncStatus(sInfo.kind, sInfo.name) : null;
    const tabBadge = sStatus ? syncBadgeHtml(sStatus).replace('class="sync-badge', 'class="ws-tab-sync sync-badge') : '';

    const icon = tab.type === 'table'
      ? '<i class="ic ic-database ws-tab-icon"></i>'
      : fileIconSvg(tab.language);

    btn.innerHTML = `${icon}<span class="ws-tab-label">${esc(tab.label)}</span>${tabBadge}`;

    const closeBtn = document.createElement('span');
    closeBtn.className = 'ws-tab-close';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeFileTab(tab.id);
    });
    btn.appendChild(closeBtn);

    btn.addEventListener('click', () => {
      // If diff panel is open, close it and switch to this file tab
      if (state.diffPanelOpen) {
        hideDiffPanel();
      }
      switchWorkspaceTab(tab.id);
    });
    // Middle-click to close
    btn.addEventListener('auxclick', (e) => {
      if (e.button === 1) { e.preventDefault(); closeFileTab(tab.id); }
    });
    // Right-click context menu
    btn.addEventListener('contextmenu', (e) => _showTabContextMenu(e, tab.id));
    frag.appendChild(btn);
  }

  // Add a "Sync Changes" tab when the diff panel is open
  if (diffActive) {
    const diffBtn = document.createElement('button');
    diffBtn.className = 'ws-tab active';
    diffBtn.dataset.tabId = '__diff__';
    diffBtn.innerHTML = `<i class="ic ic-git-pull-request ws-tab-icon"></i><span class="ws-tab-label">Sync Changes</span>`;

    const closeBtn = document.createElement('span');
    closeBtn.className = 'ws-tab-close';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      hideDiffPanel();
      renderWorkspaceTabs();
    });
    diffBtn.appendChild(closeBtn);

    diffBtn.addEventListener('click', () => {
      showDiffPanel();
    });
    frag.appendChild(diffBtn);
  }

  tabBar.innerHTML = '';
  tabBar.appendChild(frag);

  // Scroll active tab into view
  requestAnimationFrame(() => {
    const active = tabBar.querySelector('.ws-tab.active');
    if (active) active.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  });
}


// ---- Context menu ----

function _showTabContextMenu(e, tabId) {
  e.preventDefault();
  e.stopPropagation();
  _hideOverflowPanel();

  const menu = refs.wsCtxMenu;
  if (!menu) return;
  menu.innerHTML = '';

  const items = [
    { label: 'Close', action: () => closeFileTab(tabId) },
    { label: 'Close Others', action: () => _closeOtherTabs(tabId) },
    { label: 'Close to the Right', action: () => _closeTabsToRight(tabId) },
    { sep: true },
    { label: 'Close All', cls: 'danger', action: () => _closeAllTabs() },
  ];

  for (const item of items) {
    if (item.sep) {
      const sep = document.createElement('div');
      sep.className = 'ws-ctx-sep';
      menu.appendChild(sep);
      continue;
    }
    const btn = document.createElement('button');
    btn.className = 'ws-ctx-item' + (item.cls ? ' ' + item.cls : '');
    btn.textContent = item.label;
    btn.addEventListener('click', () => { _hideContextMenu(); item.action(); });
    menu.appendChild(btn);
  }

  // Position at cursor, clamp to viewport
  menu.hidden = false;
  const mw = menu.offsetWidth, mh = menu.offsetHeight;
  const x = Math.min(e.clientX, window.innerWidth - mw - 8);
  const y = Math.min(e.clientY, window.innerHeight - mh - 8);
  menu.style.left = x + 'px';
  menu.style.top = y + 'px';
  state.wsCtxMenuOpen = true;
}

function _hideContextMenu() {
  if (!state.wsCtxMenuOpen) return;
  if (refs.wsCtxMenu) refs.wsCtxMenu.hidden = true;
  state.wsCtxMenuOpen = false;
}

// ---- Bulk close helpers ----

function _closeOtherTabs(keepTabId) {
  state.workspaceTabs = state.workspaceTabs.filter(t => t.id === keepTabId);
  if (state.activeWorkspaceTab !== keepTabId) {
    switchWorkspaceTab(keepTabId);
  } else {
    renderWorkspaceTabs();
  }
}

function _closeTabsToRight(tabId) {
  const idx = state.workspaceTabs.findIndex(t => t.id === tabId);
  if (idx === -1) return;
  const removed = state.workspaceTabs.splice(idx + 1);
  const removedIds = new Set(removed.map(t => t.id));
  if (removedIds.has(state.activeWorkspaceTab)) {
    switchWorkspaceTab(tabId);
  } else {
    renderWorkspaceTabs();
  }
}

function _closeAllTabs() {
  state.workspaceTabs = [];
  switchWorkspaceTab(null);
}

// ---- Overflow dropdown ----

function _toggleOverflowPanel() {
  if (state.wsOverflowOpen) {
    _hideOverflowPanel();
    return;
  }
  _hideContextMenu();

  const panel = refs.wsOverflowPanel;
  if (!panel) return;
  panel.innerHTML = '';

  for (const tab of state.workspaceTabs) {
    const btn = document.createElement('button');
    btn.className = 'ws-overflow-item' + (state.activeWorkspaceTab === tab.id ? ' active' : '');
    btn.innerHTML = `${fileIconSvg(tab.language)}<span class="ws-overflow-item-label">${esc(tab.label)}</span>`;
    btn.addEventListener('click', () => { _hideOverflowPanel(); switchWorkspaceTab(tab.id); });
    panel.appendChild(btn);
  }

  // Position under the center zone tab bar
  const tabBar = refs.zoneCenterTabs;
  if (tabBar) {
    const rect = tabBar.getBoundingClientRect();
    panel.hidden = false;
    const pw = panel.offsetWidth;
    let left = rect.right - pw;
    if (left < 8) left = 8;
    panel.style.left = left + 'px';
    panel.style.top = (rect.bottom + 4) + 'px';
  } else {
    panel.hidden = false;
  }

  state.wsOverflowOpen = true;
}

function _hideOverflowPanel() {
  if (!state.wsOverflowOpen) return;
  if (refs.wsOverflowPanel) refs.wsOverflowPanel.hidden = true;
  if (refs.wsOverflowBtn) refs.wsOverflowBtn.classList.remove('active');
  state.wsOverflowOpen = false;
}

async function loadFileTabContent(tab) {
  try {
    const result = await api.fileContent(tab.path);
    tab._content = result.content;
    if (state.activeWorkspaceTab === tab.id) {
      renderFileViewer(tab);
    }
  } catch (e) {
    if (refs.fileViewer) refs.fileViewer.innerHTML = `<div class="search-empty">Error loading file: ${esc(e.message)}</div>`;
  }
}

/**
 * Reload content for any open workspace tab whose path matches a changed file.
 * Called by the files_changed WS handler.
 */
export function reloadChangedTabs(changedPaths) {
  for (const tab of state.workspaceTabs) {
    if (changedPaths.has(tab.path)) {
      refreshTabContent(tab.path);
    }
  }
}

/**
 * Force-refresh an open tab's content after an external file change
 * (e.g. after pulling from cloud). Disposes the existing Monaco model
 * so the editor re-creates one with the new content, then re-fetches
 * content from the server.
 *
 * Safe to call when no tab is open for the path — does nothing.
 */
export function refreshTabContent(path) {
  const tab = state.workspaceTabs.find(t => t.path === path);
  if (!tab) return;
  // Don't clobber unsaved edits when the file changes underneath us (external
  // editor save, pull, or agent write). Silently reloading would discard the
  // user's work — instead keep their edits and tell them the disk copy moved.
  if (isTabDirty(tab.id)) {
    const name = tab.label || tab.path.split('/').pop();
    notify(
      `"${name}" changed on disk — your unsaved edits are kept. Close and reopen the tab to load the new version.`,
      'info',
      { duration: 8000 },
    );
    return;
  }
  disposeTabModel(tab.id);
  tab._content = undefined;  // force re-render loading state
  loadFileTabContent(tab);
}

// ---------------------------------------------------------------------------
// File-viewer toolbar helpers
// ---------------------------------------------------------------------------

const _KIND_CHIP_LETTER = {
  script:        'S',
  calculation:   'C',
  template:      'T',
  'quote-table': 'Q',
  properties:    'P',
};

function _kindChipHtml(kind, fullLabel) {
  const letter = _KIND_CHIP_LETTER[kind];
  if (!letter) return '';
  return `<span class="fv-kind-chip" data-kind="${kind}" title="${esc(fullLabel || kind)}">${letter}</span>`;
}

const _SYNC_LABEL = {
  in_sync: 'In sync with cloud',
  local_only: 'Local only — push to cloud to sync',
  remote_only: 'Remote only — pull from cloud',
  conflict: 'Conflict — local and remote diverged',
};

function _syncStatusIconHtml(status) {
  if (!status) return '';
  const dir = status.direction || 'in_sync';
  const tip = _SYNC_LABEL[dir] || 'Sync status';
  return `<button class="fv-sync-icon" data-status="${esc(dir)}" title="${esc(tip)}" aria-label="${esc(tip)}">
    ${actionIcon('cloud')}
    <span class="status-dot"></span>
  </button>`;
}

/**
 * Build the rail action list for a tab. Returns an array of:
 *   { type, title, cssClass, run, conflictDot? }
 * The rail shows up to 4 of these from the top, plus an overflow ⋯ at the bottom.
 *
 * Slot occupancy by file kind (per spec):
 *   script:      run, validate, sync, diff
 *   calculation: validate, sync, diff
 *   template:    sync, preview, diff
 *   other:       sync, diff
 */
function _buildRailActions(tab) {
  const isPython = tab.language === 'python';
  const isScript = tab.kind === 'script';
  const isTemplate = tab.kind === 'template';
  const syncInfo = syncInfoFromPath(tab.path);
  const status = syncInfo ? getSyncStatus(syncInfo.kind, syncInfo.name) : null;
  const dir = status?.direction;

  const actions = [];

  // Run — script only
  if (isScript) {
    actions.push({
      type: 'run', title: 'Run in Script Workbench (⌘⏎)', cssClass: 'run',
      run: () => _runScriptInWorkbench(tab),
    });
  }

  // Validate — Python files (scripts and calculations)
  if (isPython) {
    actions.push({
      type: 'validate', title: 'Validate syntax (⌘⇧V)', cssClass: '',
      run: () => _validateAndUpdateBar(),
    });
  }

  // Preview — templates only
  if (isTemplate) {
    actions.push({
      type: 'preview', title: 'Preview locally', cssClass: '',
      run: () => { if (!isPreviewOpen()) showPreviewPanel(); },
    });
  }

  // Sync — contextual icon based on status
  if (dir === 'local_only') {
    actions.push({
      type: 'push', title: 'Push to cloud', cssClass: 'accent',
      run: () => _runSyncAction(tab, 'push'),
    });
  } else if (dir === 'remote_only') {
    actions.push({
      type: 'pull', title: 'Pull from cloud', cssClass: 'warn',
      run: () => _runSyncAction(tab, 'pull'),
    });
  } else if (dir === 'conflict') {
    actions.push({
      type: 'push', title: 'Resolve conflict — local & remote diverged', cssClass: 'danger',
      conflictDot: true,
      run: () => _runSyncAction(tab, 'diff'),
    });
  } else if (dir === 'in_sync') {
    actions.push({
      type: 'in_sync', title: 'In sync with cloud', cssClass: 'muted',
      run: () => notify('Already in sync with cloud', 'info'),
    });
  }

  // Diff — always available when there's a sync target
  if (syncInfo) {
    actions.push({
      type: 'diff', title: 'View diff vs cloud', cssClass: '',
      run: () => openDiffForScript(syncInfo.name, syncInfo.kind),
    });
  }

  return actions.slice(0, 4);
}

async function _runScriptInWorkbench(tab) {
  const content = getEditorContent();
  if (!content) return;
  const name = tab.label?.replace(/\.py$/, '') || 'Script';
  wsSend({ type: 'tool_call', tool: 'script_run', params: { code: content }, script_name: name });
  state.isThinking = true;
  const thinkingEl = document.getElementById('thinking');
  if (thinkingEl) thinkingEl.hidden = false;
  // Safety: if no WS response within 30s, clear thinking state so the UI doesn't lock.
  // Successful WS handlers also clear isThinking — duplicate clears are idempotent.
  setTimeout(() => {
    if (state.isThinking) {
      state.isThinking = false;
      const el = document.getElementById('thinking');
      if (el) el.hidden = true;
    }
  }, 30000);
}

export function runActiveScriptInWorkbench() {
  if (state.isThinking) return;  // already running — guard against repeat-fire and double-click
  const tab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
  if (!tab || tab.kind !== 'script') return;
  _runScriptInWorkbench(tab);
}

export function validateActiveTab() {
  const tab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
  if (!tab || tab.language !== 'python') return;
  _validateAndUpdateBar();
}

async function _runSyncAction(tab, kind) {
  const info = syncInfoFromPath(tab.path);
  if (!info) return;
  const apiKind = info.kind;
  if (kind === 'push') {
    const ok = await confirmPushToCloud(info.name, state.tenant);
    if (ok) await pushWithFeedback(apiKind, info.name);
  } else if (kind === 'pull') {
    const ok = await confirmPullFromCloud(info.name);
    if (ok) await pullWithFeedback(apiKind, info.name);
  } else if (kind === 'diff') {
    openDiffForScript(info.name, apiKind);
  }
}

async function _validateAndUpdateBar() {
  updateValidationBar('validating');
  const result = await validateCurrentTab();
  if (result?.valid) updateValidationBar('valid');
  else updateValidationBar('errors', result?.errors || []);
}

async function renderFileViewer(tab) {
  if (!refs.fileViewer) return;
  if (!tab._content && tab._content !== "") {
    refs.fileViewer.innerHTML = '<div class="search-empty">Loading...</div>';
    return;
  }

  const isPython = tab.language === 'python';
  const syncInfo = syncInfoFromPath(tab.path);
  const status = syncInfo ? getSyncStatus(syncInfo.kind, syncInfo.name) : null;

  // Outer shell: rail | (header + body)
  const shell = document.createElement('div');
  shell.className = 'fv-shell';

  // ── Rail (left) ──
  const rail = document.createElement('div');
  rail.className = 'fv-rail';
  const railActions = _buildRailActions(tab);
  for (const a of railActions) {
    const btn = document.createElement('button');
    btn.className = 'fv-rail-btn ' + (a.cssClass || '');
    btn.title = a.title;
    btn.dataset.action = a.type;
    btn.innerHTML = actionIcon(a.type);
    if (a.conflictDot) {
      const dot = document.createElement('span');
      dot.className = 'conflict-dot';
      btn.appendChild(dot);
    }
    btn.addEventListener('click', () => a.run());
    rail.appendChild(btn);
  }

  const railSpacer = document.createElement('div');
  railSpacer.className = 'fv-rail-spacer';
  rail.appendChild(railSpacer);

  // Overflow ⋯ — bottom of rail
  const overflowBtn = document.createElement('button');
  overflowBtn.className = 'fv-rail-btn';
  overflowBtn.title = 'More actions (⌘⇧M)';
  overflowBtn.dataset.action = 'overflow';
  overflowBtn.innerHTML = actionIcon('overflow');
  overflowBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    _toggleOverflowMenu(tab, overflowBtn);
  });
  rail.appendChild(overflowBtn);

  // ── Editor column (right of rail) ──
  const col = document.createElement('div');
  col.className = 'fv-col';

  // Header: kind chip · filename · path · sync icon
  const header = document.createElement('div');
  header.className = 'file-viewer-header';
  header.innerHTML = `
    ${_kindChipHtml(tab.kind, tab.kindLabel)}
    <span class="fv-filename">${esc(tab.label)}</span>
    <span class="fv-grow"></span>
    <span class="fv-path">${esc(tab.path)}</span>
    ${_syncStatusIconHtml(status)}
  `;

  // Wire sync icon click — opens diff (or shows toast when in_sync)
  const syncIconBtn = header.querySelector('.fv-sync-icon');
  if (syncIconBtn) {
    syncIconBtn.addEventListener('click', () => {
      const dir = status?.direction;
      if (!dir || dir === 'in_sync') {
        notify('Already in sync with cloud', 'info');
      } else if (dir === 'local_only') {
        _runSyncAction(tab, 'push');
      } else if (dir === 'remote_only') {
        _runSyncAction(tab, 'pull');
      } else if (dir === 'conflict') {
        _runSyncAction(tab, 'diff');
      }
    });
  }
  col.appendChild(header);

  // Hosted: persistent read-only strip — the editor is a reader there (Monaco mounts
  // readOnly; the agent applies changes so every edit stays audited and undoable), and
  // the inline CTA hands off to the composer instead of dead-ending the save intent.
  if (getAuthMode() === 'hosted') {
    const roBanner = document.createElement('div');
    roBanner.className = 'hosted-ro-banner';
    roBanner.innerHTML = `<span class="ic ic-lock"></span>
      <span>Read only. In the hosted app, the agent edits files so every change stays audited and undoable.</span>
      <button type="button" class="ro-cta">Ask the agent to edit this file</button>`;
    roBanner.querySelector('.ro-cta').addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('agent-prefill-composer', {
        detail: { text: `Please edit ${tab.path}: ` },
      }));
    });
    col.appendChild(roBanner);
  }

  // Validation bar (Python only)
  if (isPython) {
    const vbarDiv = document.createElement('div');
    vbarDiv.className = 'validation-bar';
    vbarDiv.id = 'validationBar';
    vbarDiv.hidden = true;
    vbarDiv.innerHTML = `<span class="validation-bar-icon"></span>
      <span class="validation-bar-message"></span>
      <button class="validation-bar-close" title="Dismiss"><i class="ic ic-sm ic-x"></i></button>`;
    col.appendChild(vbarDiv);
    vbarDiv.querySelector('.validation-bar-close')?.addEventListener('click', () => {
      document.getElementById('validationBar')?.setAttribute('hidden', '');
    });
  }

  // Body: Monaco + structure panel
  const mainContent = document.createElement('div');
  mainContent.className = 'file-viewer-main';
  const body = document.createElement('div');
  body.className = 'file-viewer-body';
  mainContent.appendChild(body);

  if (isPython) {
    const sp = createStructurePanel();
    mainContent.appendChild(sp.element);
    if (tab._structurePanelHidden === true) sp.element.hidden = true;
    sp.element.addEventListener('sp-closed', () => {
      tab._structurePanelHidden = true;
    });
    tab._structurePanel = sp;
  }

  col.appendChild(mainContent);

  shell.appendChild(rail);
  shell.appendChild(col);

  refs.fileViewer.innerHTML = '';
  refs.fileViewer.appendChild(shell);

  // Mount Monaco
  const monacoMounted = await mountEditor(body, tab);
  if (!monacoMounted) {
    body.appendChild(renderCodeLines(tab._content, tab.language, tab.highlightLine));
    updateStatusBarLang(tab.language);
    requestAnimationFrame(() => {
      if (tab.highlightLine) {
        const hl = body.querySelector('.code-line.highlight');
        if (hl) hl.scrollIntoView({ block: 'center' });
      } else if (tab.scrollTop) {
        body.scrollTop = tab.scrollTop;
      }
    });
  }

  if (isPython && tab._structurePanel) {
    const sp = tab._structurePanel;
    renderStructurePanel(sp.body, tab._content, tab.language, (line) => revealLine(line));
    if (monacoMounted) {
      if (typeof tab._spDispose === 'function') tab._spDispose();
      tab._spDispose = onContentChange((newContent) => {
        renderStructurePanel(sp.body, newContent, tab.language, (line) => revealLine(line));
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Overflow menu (rail ⋯ button)
// ---------------------------------------------------------------------------

let _activeOverflowMenu = null;

function _closeOverflowMenu() {
  if (_activeOverflowMenu && _activeOverflowMenu.parentNode) {
    _activeOverflowMenu.parentNode.removeChild(_activeOverflowMenu);
  }
  _activeOverflowMenu = null;
  document.removeEventListener('mousedown', _onOverflowDocMouseDown, true);
  document.removeEventListener('keydown', _onOverflowKeydown, true);
}

function _onOverflowDocMouseDown(e) {
  if (_activeOverflowMenu && !_activeOverflowMenu.contains(e.target)) _closeOverflowMenu();
}
function _onOverflowKeydown(e) {
  if (e.key === 'Escape') _closeOverflowMenu();
}

export function openOverflowMenuForActiveTab() {
  const overflowBtn = document.querySelector('.fv-rail [data-action="overflow"]');
  const tab = state.workspaceTabs.find(t => t.id === state.activeWorkspaceTab);
  if (overflowBtn && tab) _toggleOverflowMenu(tab, overflowBtn);
}

function _toggleOverflowMenu(tab, anchorBtn) {
  if (_activeOverflowMenu) { _closeOverflowMenu(); return; }
  const railTypes = new Set(_buildRailActions(tab).map(a => a.type));
  const allActions = buildFileActionsForPath(tab.path);
  const overflowActions = allActions.filter(a => !railTypes.has(a.type));

  if (!overflowActions.length) return;

  const menu = document.createElement('div');
  menu.className = 'fv-overflow-menu';
  for (const a of overflowActions) {
    const row = document.createElement('button');
    row.className = 'fv-overflow-row';
    row.innerHTML = `<span class="fv-overflow-ico">${actionIcon(a.type)}</span><span>${esc(a.label)}</span>`;
    row.addEventListener('click', (ev) => {
      ev.stopPropagation();
      _closeOverflowMenu();
      a.run();
    });
    menu.appendChild(row);
  }

  document.body.appendChild(menu);
  _activeOverflowMenu = menu;

  const r = anchorBtn.getBoundingClientRect();
  const mw = menu.offsetWidth || 220;
  const mh = menu.offsetHeight || 200;
  menu.style.left = Math.min(r.right + 6, window.innerWidth - mw - 8) + 'px';
  menu.style.top  = Math.min(r.top, window.innerHeight - mh - 8) + 'px';

  document.addEventListener('mousedown', _onOverflowDocMouseDown, true);
  document.addEventListener('keydown', _onOverflowKeydown, true);
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

// ===========================================================================
// Sidebar Search — VS Code-style search panel in the sidebar
// ===========================================================================

let sbQuery = '';
let sbRegex = false;
let sbCaseSensitive = false;
let sbScope = 'all';
let sbResults = null;
let sbDebounce = null;
let sbActiveIdx = -1;
let sbMatchRows = [];

export function focusSidebarSearch() {
  if (refs.sbSearchInput) {
    // Small delay to let the sidebar mode become visible first
    requestAnimationFrame(() => {
      refs.sbSearchInput.focus();
      refs.sbSearchInput.select();
    });
  }
}

function sbScheduleSearch() {
  clearTimeout(sbDebounce);
  const q = refs.sbSearchInput.value.trim();
  sbQuery = q;
  if (!q) {
    sbResults = null;
    sbActiveIdx = -1;
    sbMatchRows = [];
    refs.sbSearchResults.innerHTML = '<div class="search-empty">Type to search across all files</div>';
    refs.sbSearchStats.textContent = '';
    return;
  }
  sbDebounce = setTimeout(sbDoSearch, 300);
}

async function sbDoSearch() {
  const q = sbQuery;
  if (!q) return;

  // Hosted latency expectation + spinner \u2014 same reasoning as doSearch() above.
  refs.sbSearchResults.innerHTML = getAuthMode() === 'hosted'
    ? '<div class="search-empty sb-searching"><span class="pl-field-spinner"></span> Searching your live SAP files. This can take a few seconds.</div>'
    : '<div class="search-empty sb-searching">Searching\u2026</div>';
  refs.sbSearchStats.textContent = '';

  try {
    const data = await api.searchFiles(q, {
      regex: sbRegex,
      caseSensitive: sbCaseSensitive,
      kind: sbScope,
      max: 200,
    });
    if (sbQuery !== q) return; // stale
    sbResults = data;
    sbRenderResults(data);
  } catch (e) {
    refs.sbSearchResults.innerHTML = `<div class="search-empty">Error: ${esc(e.message)}</div>`;
    refs.sbSearchStats.textContent = '';
  }
}

function sbRenderResults(data) {
  const frag = document.createDocumentFragment();
  sbMatchRows = [];
  sbActiveIdx = -1;

  if (!data.matches || data.matches.length === 0) {
    refs.sbSearchResults.innerHTML = '<div class="search-empty">No results found</div>';
    refs.sbSearchStats.textContent = '';
    return;
  }

  // Group by file
  const groups = new Map();
  for (const m of data.matches) {
    const key = m.path;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  }

  for (const [path, matches] of groups) {
    const first = matches[0];

    // Collapsible file group
    const group = document.createElement('div');
    group.className = 'sb-file-group';

    const header = document.createElement('div');
    header.className = 'sb-file-header';
    header.innerHTML = `<i class="ic ic-sm ic-chevron-right sb-caret"></i>
      ${fileIconSvg(first.language)}
      <span class="sb-file-name">${esc(first.file)}</span>
      <span class="sb-file-count">${matches.length}</span>`;

    const matchList = document.createElement('div');
    matchList.className = 'sb-match-list';

    // Toggle collapse
    let collapsed = false;
    header.addEventListener('click', () => {
      collapsed = !collapsed;
      matchList.hidden = collapsed;
      header.classList.toggle('collapsed', collapsed);
    });

    // File header click also opens file (double-click)
    header.addEventListener('dblclick', () => {
      openFileTab(first.path, first.file, first.language);
    });

    for (const m of matches) {
      const row = document.createElement('div');
      row.className = 'sb-match-row';
      row.innerHTML = `<span class="sb-match-num">${m.line}</span><span class="sb-match-text">${sbHighlight(m.text, sbQuery, sbRegex, sbCaseSensitive)}</span>`;
      const matchInfo = { path: m.path, line: m.line, language: m.language, file: m.file };
      row.addEventListener('click', () => {
        // Highlight active row
        if (sbActiveIdx >= 0 && sbMatchRows[sbActiveIdx]) {
          sbMatchRows[sbActiveIdx].classList.remove('active');
        }
        sbActiveIdx = sbMatchRows.indexOf(row);
        row.classList.add('active');
        openFileTab(matchInfo.path, matchInfo.file, matchInfo.language, matchInfo.line);
      });
      sbMatchRows.push(row);
      matchList.appendChild(row);
    }

    group.appendChild(header);
    group.appendChild(matchList);
    frag.appendChild(group);
  }

  refs.sbSearchResults.innerHTML = '';
  refs.sbSearchResults.appendChild(frag);

  const total = data.matches.length;
  const fileCount = groups.size;
  const truncLabel = data.truncated ? '+' : '';
  // Hosted: name the live-corpus cap (compact sidebar variant of the dialog's copy).
  refs.sbSearchStats.textContent = (data.truncated && getAuthMode() === 'hosted')
    ? `${total}+ in ${fileCount} files (first 80 searched)`
    : `${total}${truncLabel} matches in ${fileCount} file${fileCount !== 1 ? 's' : ''}`;
}

function sbHighlight(text, query, isRegex, caseSensitive) {
  const escaped = esc(text);
  try {
    const flags = caseSensitive ? 'g' : 'gi';
    const pattern = isRegex ? query : query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(pattern, flags));
    const matches = text.match(new RegExp(pattern, flags)) || [];
    let html = '';
    for (let i = 0; i < parts.length; i++) {
      html += esc(parts[i]);
      if (i < matches.length) html += `<mark>${esc(matches[i])}</mark>`;
    }
    return html;
  } catch {
    return escaped;
  }
}

function sbHandleKeydown(e) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (sbMatchRows.length === 0) return;
    const next = sbActiveIdx < sbMatchRows.length - 1 ? sbActiveIdx + 1 : 0;
    sbMatchRows[next].click();
    sbMatchRows[next].scrollIntoView({ block: 'nearest' });
    return;
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (sbMatchRows.length === 0) return;
    const prev = sbActiveIdx > 0 ? sbActiveIdx - 1 : sbMatchRows.length - 1;
    sbMatchRows[prev].click();
    sbMatchRows[prev].scrollIntoView({ block: 'nearest' });
    return;
  }
  if (e.key === 'Escape') {
    refs.sbSearchInput.blur();
  }
}

export function initSidebarSearch() {
  if (!refs.sbSearchInput) return;

  refs.sbSearchInput.addEventListener('input', sbScheduleSearch);
  refs.sbSearchInput.addEventListener('keydown', sbHandleKeydown);

  // Regex / Case toggles
  refs.sbToggleRegex.addEventListener('click', () => {
    sbRegex = !sbRegex;
    refs.sbToggleRegex.classList.toggle('active', sbRegex);
    sbScheduleSearch();
  });
  refs.sbToggleCase.addEventListener('click', () => {
    sbCaseSensitive = !sbCaseSensitive;
    refs.sbToggleCase.classList.toggle('active', sbCaseSensitive);
    sbScheduleSearch();
  });

  // Scope tabs
  for (const tab of document.querySelectorAll('.sb-scope')) {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.sb-scope').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      sbScope = tab.dataset.scope;
      sbScheduleSearch();
    });
  }
}

// ===========================================================================

export function initSearch() {
  if (!refs.searchDialog) return;

  // Search input
  refs.searchInput.addEventListener('input', scheduleSearch);
  refs.searchInput.addEventListener('keydown', handleSearchKeydown);

  // Close button
  refs.searchCloseBtn.addEventListener('click', closeSearch);

  // Dialog backdrop click to close
  refs.searchDialog.addEventListener('click', (e) => {
    if (e.target === refs.searchDialog) closeSearch();
  });

  // Toggles
  refs.searchToggleRegex.addEventListener('click', () => {
    searchRegex = !searchRegex;
    refs.searchToggleRegex.classList.toggle('active', searchRegex);
    scheduleSearch();
  });
  refs.searchToggleCase.addEventListener('click', () => {
    searchCaseSensitive = !searchCaseSensitive;
    refs.searchToggleCase.classList.toggle('active', searchCaseSensitive);
    scheduleSearch();
  });

  // Scope tabs
  for (const tab of refs.searchDialog.querySelectorAll('.search-scope-tab')) {
    tab.addEventListener('click', () => {
      refs.searchDialog.querySelectorAll('.search-scope-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      searchScope = tab.dataset.scope;
      scheduleSearch();
    });
  }

  // File extension filters
  for (const cb of refs.searchDialog.querySelectorAll('.search-filter-check input')) {
    cb.addEventListener('change', () => {
      extFilters[cb.dataset.ext] = cb.checked;
      if (searchResults) renderResults(searchResults);
    });
  }

  // Double-click on preview → open file
  refs.searchPreviewBody.addEventListener('dblclick', () => {
    if (!previewPath) return;
    const matchInfo = getMatchInfoForRow(activeMatchIdx);
    if (matchInfo) openFromSearch(matchInfo);
  });

  // Split pane resize
  initSplitResize();

  // Click-outside to dismiss context menu and overflow panel
  document.addEventListener('click', (e) => {
    if (state.wsCtxMenuOpen && refs.wsCtxMenu && !refs.wsCtxMenu.contains(e.target)) _hideContextMenu();
    if (state.wsOverflowOpen && refs.wsOverflowPanel && !refs.wsOverflowPanel.contains(e.target)) _hideOverflowPanel();
  });

  // Global keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    // Escape → dismiss context menu / overflow panel
    if (e.key === 'Escape') {
      if (state.wsCtxMenuOpen) { _hideContextMenu(); return; }
      if (state.wsOverflowOpen) { _hideOverflowPanel(); return; }
    }
    // Cmd/Ctrl+Shift+F → open sidebar search
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'F') {
      e.preventDefault();
      // Activate search mode via the activity bar button
      const searchBtn = document.querySelector('.ab-btn[data-mode="search"]');
      if (searchBtn) searchBtn.click();
      return;
    }
    // Cmd/Ctrl+W → close active file tab
    if ((e.metaKey || e.ctrlKey) && e.key === 'w' && state.activeWorkspaceTab) {
      e.preventDefault();
      closeFileTab(state.activeWorkspaceTab);
      return;
    }
    // Ctrl+Tab / Ctrl+Shift+Tab → cycle file tabs
    if (e.ctrlKey && e.key === 'Tab') {
      e.preventDefault();
      const allIds = state.workspaceTabs.map(t => t.id);
      if (allIds.length <= 1) return;
      const currentIdx = allIds.indexOf(state.activeWorkspaceTab);
      const nextIdx = e.shiftKey
        ? (currentIdx - 1 + allIds.length) % allIds.length
        : (currentIdx + 1) % allIds.length;
      switchWorkspaceTab(allIds[nextIdx]);
    }
  });
}
